import { useSignal } from '@preact/signals-react/runtime';
import { useCallback, useRef } from 'react';

import type { ThinkingMessage, ThinkingMessageItem } from './types';

export function useChatThinking() {
  const thinkings = useSignal<ThinkingMessageItem[]>([]);

  const thinkingBuffer = useRef<{
    accumulator: string;
    timeoutId: NodeJS.Timeout | null;
    isProcessing: boolean;
  }>({
    accumulator: '',
    timeoutId: null,
    isProcessing: false,
  });

  const thinkingQueue = useRef<{
    queue: string[];
    isProcessing: boolean;
    timeoutId: NodeJS.Timeout | null;
  }>({
    queue: [],
    isProcessing: false,
    timeoutId: null,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const processThinkingQueue = useCallback(() => {
    if (
      thinkingQueue.current.isProcessing ||
      thinkingQueue.current.queue.length === 0
    ) {
      return;
    }

    thinkingQueue.current.isProcessing = true;
    const nextThinking = thinkingQueue.current.queue.shift();

    if (!nextThinking) {
      thinkingQueue.current.isProcessing = false;
      return;
    }

    thinkings.value = [
      ...thinkings.value,
      {
        content: nextThinking,
        status: 'finished',
      },
    ];

    thinkingQueue.current.timeoutId = setTimeout(() => {
      thinkingQueue.current.isProcessing = false;
      thinkingQueue.current.timeoutId = null;
      processThinkingQueue();
    }, 1500);
  }, []);

  const handleThinkingMessage = useCallback(
    (data: ThinkingMessage) => {
      thinkingBuffer.current.accumulator += data.message.content;

      if (data.message.status === 'finished') {
        const completeThinking = thinkingBuffer.current.accumulator.trim();
        if (completeThinking) {
          thinkingQueue.current.queue.push(completeThinking);
          processThinkingQueue();
        }
        thinkingBuffer.current.accumulator = '';
      }
    },
    [processThinkingQueue],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const clearThinking = useCallback(() => {
    thinkings.value = [];

    if (thinkingBuffer.current.timeoutId) {
      clearTimeout(thinkingBuffer.current.timeoutId);
      thinkingBuffer.current.timeoutId = null;
    }
    thinkingBuffer.current.accumulator = '';
    thinkingBuffer.current.isProcessing = false;

    if (thinkingQueue.current.timeoutId) {
      clearTimeout(thinkingQueue.current.timeoutId);
      thinkingQueue.current.timeoutId = null;
    }
    thinkingQueue.current.queue = [];
    thinkingQueue.current.isProcessing = false;
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const setInitialThinking = useCallback(() => {
    thinkings.value = [
      {
        content: 'Pensando...',
        status: 'running',
      },
    ];
  }, []);

  return {
    thinkings,
    handleThinkingMessage,
    clearThinking,
    setInitialThinking,
  };
}
